import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, CalendarDays } from "lucide-react";
import { TIDYCAL_URL, PORTRAIT_URL, scrollToSection } from "@/constants";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <div className="overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </div>
);

const STATS = [
  ["30+ Years", "Teaching technology and leading learning experiences"],
  ["Fortune 500", "Technology consulting experience"],
  ["Global Stages", "United States · Europe · Corporate · Education"],
  ["White House", "Invited speaker and technology educator"],
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative pt-36 pb-24 lg:pt-44 lg:pb-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-accent mb-8"
            data-testid="hero-overline"
          >
            AI · Workforce Development · Professional Learning · Technology Leadership
          </motion.p>

          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.95] font-medium">
            <MaskedLine delay={0.25}>Prepare Your People</MaskedLine>
            <MaskedLine delay={0.4} className="italic text-primary">for What's Next.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
            className="mt-10 max-w-xl text-base sm:text-lg lg:text-xl leading-relaxed font-light text-foreground/80"
            data-testid="hero-subtitle"
          >
            Phillip Banks Sr. helps companies, school districts, and leadership teams
            turn AI and emerging technology into practical advantage through
            high-impact professional development, workshops, and executive training.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-5 max-w-xl text-sm sm:text-base leading-relaxed text-foreground/60"
            data-testid="hero-supporting-line"
          >
            More than 30 years of technology, consulting, teaching, and stage
            experience — translated into practical strategies your people can use
            immediately.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
            className="mt-12 flex flex-wrap items-center gap-6"
          >
            <button
              onClick={() => scrollToSection("#solutions")}
              data-testid="hero-explore-button"
              className="inline-flex items-center gap-3 rounded-full bg-primary text-primary-foreground px-8 py-4 font-semibold hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/20 transition-transform duration-300"
            >
              <ArrowDown className="w-5 h-5" />
              Explore Professional Development
            </button>
            <a
              href={TIDYCAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-button"
              className="inline-flex items-center gap-3 rounded-full border border-foreground/20 px-8 py-4 font-semibold hover:border-primary hover:text-primary transition-colors duration-300"
            >
              <CalendarDays className="w-5 h-5" />
              Book a Strategy Call
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-8"
            data-testid="hero-stats"
          >
            {STATS.map(([big, small]) => (
              <div key={big}>
                <div className="font-display text-xl md:text-2xl">{big}</div>
                <div className="text-xs md:text-sm text-foreground/60 mt-2 leading-snug">{small}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
            className="relative"
          >
            <div className="absolute -top-8 -right-8 w-full h-full rounded-[2.5rem] bg-primary/15 rotate-3" aria-hidden="true" />
            <motion.div style={{ y: imgY }} className="relative">
              <img
                src={PORTRAIT_URL}
                alt="Phillip Banks Sr."
                data-testid="hero-portrait"
                className="relative rounded-[2.5rem] w-full aspect-[4/5] object-cover object-top border border-[#D8D4CA]"
              />
            </motion.div>
            <motion.svg
              viewBox="0 0 120 120"
              animate={{ rotate: 360 }}
              transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
              className="motion-safe-only absolute -bottom-10 -left-10 w-32 h-32 md:w-40 md:h-40 text-foreground/70"
              aria-hidden="true"
            >
              <defs>
                <path id="badge-circle" d="M60,60 m-44,0 a44,44 0 1,1 88,0 a44,44 0 1,1 -88,0" />
              </defs>
              <circle cx="60" cy="60" r="30" className="fill-primary" />
              <text className="fill-current" style={{ fontSize: "10.5px", letterSpacing: "2.2px", fontFamily: "JetBrains Mono, monospace" }}>
                <textPath href="#badge-circle">SPEAKER • EDUCATOR • STRATEGIST • TECHNOLOGIST •</textPath>
              </text>
            </motion.svg>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
